import React, { useState, useEffect } from "react";
import moment from "moment";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";

import { ReactComponent as DollarSignIcon } from "../../../assets/Icons/dollar-sign.svg";
import { ReactComponent as FeeIcon } from "../../../assets/Icons/fee.svg";

import { ReactComponent as HouseIcon } from "../../../assets/Icons/house.svg";
import { ReactComponent as CorthouseIcon } from "../../../assets/Icons/corthouse.svg";
import { ReactComponent as LicenseVarificationIcon } from "../../../assets/Icons/license-varification.svg";
import { useUserProfile } from "../../../contexts/UserProfile";
import { useCommon } from "../../../contexts/common";
import {
  treatment_program,
  psypact,
  inperson,
  has_video,
} from "../../../constants/common";
import AgeServed from "./components/AgeServed";
import ClientTypes from "./components/ClientTypes";
import { useSearchParams } from "react-router-dom";
import CenterOverview from "./components/CenterOverview";
import PaymentInformation from "./components/PaymentInformation";
import Faq from "./components/Faq";
import UserCard from "../../../components/UserCard";
import ReferalModal from "../../../components/ReferalModal";
import SendToFriendModal from "../../../components/SendToFriendModal";
import ConnectionsModal from "../../../components/ConnectionsModal";

const ProfileContent = () => {
  const { data } = useUserProfile();
  const { licensesList, fetchLicensesList } = useCommon();

  useEffect(() => {
    fetchLicensesList();
  }, []);

  const getLicenseName = (licenseTypeId) => {
    const license = licensesList?.find(
      (license) => license.id === licenseTypeId,
    );
    return license?.name || "";
  };

  const memberships =
    data.credentialTypes?.filter((item) => item.type === "membership") || [];
  const licenses =
    data.credentialTypes?.filter((item) => item.type === "license") || [];
  const prelicense =
    data.credentialTypes?.filter((item) => item.type === "pre-license") || [];

  const prescribers =
    data.treatment_modalities?.filter((item) => item?.type === "prescribers") ||
    [];

  const [searchParams] = useSearchParams();
  const account_type = searchParams.get("account_type");

  const [activeUser, setActiveUser] = useState({});
  const [openReferral, setOpenReferral] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [openOtherNetwork, setOpenOtherNetwork] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleOpenReferralModal = (data) => {
    setActiveUser(data);
    setOpenReferral(true);
  };

  const handleCloseReferralModal = () => {
    setOpenReferral(false);
  };

  const handleOpenShareModal = (data) => {
    setActiveUser(data);
    setOpenShare(true);
  };

  const handleCloseShareModal = () => {
    setOpenShare(false);
  };

  const handleOpenOtherConnection = (data) => {
    setSelectedUser(data);
    setOpenOtherNetwork(true);
  };

  const handleCloseConnections = () => {
    setOpenOtherNetwork(false);
  };

  const isProviderDemographics =
    data.gender?.length ||
    data.language?.length ||
    data.sexual_orientation?.length ||
    data.race?.length ||
    data.religion?.length;

  const calculateProviders = () => {
    return data?.membersList.length;
  };

  const getOldestDegree = () => {
    let minYear = Infinity;
    data.degrees?.forEach((record) => {
      const practiceYear = parseInt(record.practice_year, 10);
      if (practiceYear < minYear) {
        minYear = practiceYear;
      }
    });
    return minYear;
  };

  const profileDemographics = (data, label, key) => (
    <Paper
      sx={{
        px: 3,
        pt: 3,
        pb: 1.5,
        mb: 2,
      }}
    >
      <Typography variant="h5" fontWeight={600} mb={3}>
        {label}
      </Typography>
      <Box
        component="ul"
        sx={{
          columns: 2,
          pl: 2,
          m: 0,
        }}
      >
        {data?.map((option) => (
          <Typography variant="body1" component="li" key={option.id} mb={1.5}>
            {key ? option[key]?.name : option?.name}
          </Typography>
        ))}
      </Box>
    </Paper>
  );

  return (
    <>
      {data?.info?.about_me ? (
        <Paper
          sx={{
            p: 3,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            About
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
            {data?.info?.about_me}
          </Typography>
        </Paper>
      ) : null}
      {account_type === treatment_program ? <CenterOverview /> : <></>}
      {data.client_types?.length > 0 || data.info?.age_served ? (
        <Paper
          sx={{
            p: 3,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            Client Focus
          </Typography>
          <Grid container spacing={1} alignItems="stretch">
            {data.client_types?.length > 0 ? (
              <Grid item xs={12} sm={6}>
                <ClientTypes />
              </Grid>
            ) : null}
            {data.info?.age_served ? (
              <Grid item xs={12} sm={6}>
                <AgeServed />
              </Grid>
            ) : null}
          </Grid>
        </Paper>
      ) : null}
      {data.specialties?.length > 0 ? (
        <Paper
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            Specialties
          </Typography>
          <Box
            component="ul"
            sx={{
              columns: 2,
              pl: 2,
              m: 0,
            }}
          >
            {data.specialties?.map((item) => (
              <Typography variant="body1" component="li" key={item.id} mb={1.5}>
                {item?.name}
              </Typography>
            ))}
          </Box>
        </Paper>
      ) : null}
      {data.treatment_modalities?.length > 0 ? (
        <Paper
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            mb: 2,
          }}
        >
          {Boolean(
            data.treatment_modalities?.find(
              (item) => item?.type === "therapist",
            ),
          ) ? (
            <>
              <Typography variant="h5" fontWeight={600} mb={3}>
                Treatment Modalities
              </Typography>
              <Box
                component="ul"
                sx={{
                  columns: 2,
                  pl: 2,
                  m: 0,
                }}
              >
                {data.treatment_modalities?.reduce((acc, item) => {
                  if (item?.type === "therapist") {
                    return [
                      ...acc,
                      <Typography
                        variant="body1"
                        component="li"
                        key={item.id}
                        mb={1.5}
                      >
                        {item?.name}
                      </Typography>,
                    ];
                  }
                  return acc;
                }, [])}
              </Box>
            </>
          ) : null}
          {Boolean(prescribers?.length) ? <Divider sx={{ my: 3 }} /> : null}
          {Boolean(prescribers?.length) &&
            Boolean(data?.info?.has_medication_prescriber) ? (
            <>
              <Typography variant="h5" fontWeight={600} mb={3}>
                Psychiatric
              </Typography>
              <Box
                component="ul"
                sx={{
                  columns: 2,
                  pl: 2,
                  m: 0,
                }}
              >
                {data.treatment_modalities?.reduce((acc, item) => {
                  if (item?.type === "prescribers") {
                    return [
                      ...acc,
                      <Typography
                        variant="body1"
                        component="li"
                        key={item.id}
                        mb={1.5}
                      >
                        {item?.name}
                      </Typography>,
                    ];
                  }
                  return acc;
                }, [])}
              </Box>
            </>
          ) : null}
        </Paper>
      ) : null}
      {data.info?.inperson_session || data.info?.has_video_provided ? (
        <Paper
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            Session Format
          </Typography>
          <Box
            component="ul"
            sx={{
              columns: 2,
              pl: 2,
              m: 0,
            }}
          >
            {data.info?.inperson_session ? (
              <Typography variant="body1" component="li" mb={1.5}>
                {inperson.label}
              </Typography>
            ) : null}
            {data.info?.has_video_provided ? (
              <Typography variant="body1" component="li" mb={1.5}>
                {has_video.label}
              </Typography>
            ) : null}
          </Box>
        </Paper>
      ) : null}
      {data.fees?.length > 0 ||
        data.insurance?.length > 0 ||
        data.private_pay_only ? (
        <Paper
          sx={{
            px: 3,
            pt: 3,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            Finances
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}
          >
            {data.fees?.length > 0 ? (
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Session Fees
                </Typography>
                {data.fees?.map((item) => (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      mb: 3,
                    }}
                    key={item.id}
                  >
                    <FeeIcon />
                    <Box>
                      <Typography variant="body1" lineHeight="24px">
                        {item.feedata?.name} Fee:
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        display="flex"
                        alignItems="center"
                      >
                        {item.fee?.includes(",") ? (
                          item.fee?.split(",").map((feeItem, index) => (
                            <Box
                              component="span"
                              mr={1}
                              display="flex"
                              alignItems="center"
                              key={index}
                            >
                              {index === 0 ? "From:" : "To:"}
                              <DollarSignIcon /> {feeItem}
                            </Box>
                          ))
                        ) : (
                          <>
                            <DollarSignIcon /> {item.fee}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null}
            {data.insurance?.length > 0 ? (
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Accepted Insurance Plans
                </Typography>
                {data.insurance?.map((item) => (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 3,
                    }}
                    key={item.id}
                  >
                    <Box
                      sx={{
                        width: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <HouseIcon />
                    </Box>
                    <Typography variant="body1" lineHeight="24px">
                      {item?.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}

            {data?.private_pay_only ? (
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Private Pay
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Paper>
      ) : null}
      {data.degrees?.length > 0 || data.credentialTypes?.length > 0 || data.info?.psypact_provider ? (
        <Paper
          sx={{
            px: 3,
            pt: 3,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            Qualifications
          </Typography>
          {data.degrees?.length > 0 ? (
            <>
              {/* {Boolean(data.degrees[0]?.practice_year) ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <CorthouseIcon />
                  <Typography variant="body1">
                    In Practice for{" "}
                    {moment().diff(
                      moment(getOldestDegree().toString()),
                      "years"
                    )}{" "}
                    Years
                  </Typography>
                </Box>
              ) : null} */}
              <Paper
                sx={{
                  px: 3,
                  pt: 3,
                  pb: 1.5,
                  mb: 5,
                }}
              >
                <Typography variant="h5" fontWeight={600} mb={3}>
                  Education
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    columns: 2,
                    pl: 2,
                    m: 0,
                  }}
                >
                  {data.degrees?.map((item) => (
                    <Typography
                      variant="body1"
                      component="li"
                      key={item.id}
                      mb={1.5}
                    >
                      {item?.degree?.name}{" "}
                      <Typography variant="body2" color="border.dark">
                        {item?.college_name} / {item.degree_year}
                      </Typography>
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </>
          ) : null}
          {Boolean(
            licenses?.length && licenses.some((license) => license.type_id),
          ) ? (
            <Paper
              sx={{
                px: 3,
                pt: 3,
                pb: 1.5,
                mb: 5,
              }}
            >
              <Typography variant="h5" fontWeight={600} mb={3}>
                License
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 2,
                  m: 0,
                }}
              >
                {licenses.map((item) =>
                  item.type_id ? (
                    <Typography
                      variant="body1"
                      component="li"
                      key={item.id}
                      mb={1.5}
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {getLicenseName(item?.type_id)}
                      <br />
                      {item?.data?.board} | {item?.state?.name} |{" "}
                      {item?.data?.number}
                    </Typography>
                  ) : null,
                )}
              </Box>
            </Paper>
          ) : null}
          {Boolean(prelicense?.length) ? (
            <Paper
              sx={{
                px: 3,
                pt: 3,
                pb: 1.5,
                mb: 5,
              }}
            >
              <Typography variant="h5" fontWeight={600} mb={3}>
                Pre-Licensure
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 2,
                  m: 0,
                  whiteSpace: "pre-line",
                }}
              >
                {prelicense.map((item) => (
                  <Typography
                    variant="body1"
                    component="li"
                    key={item.id}
                    mb={1.5}
                  >
                    Supervisor: {item?.data?.supervisor_name}
                    <br />
                    {getLicenseName(item?.type_id)} | {item?.state?.name}
                  </Typography>
                ))}
              </Box>
            </Paper>
          ) : null}
          {data.info?.psypact_provider ? (
            <Paper
              sx={{
                px: 3,
                pt: 3,
                pb: 1.5,
                mb: 2,
              }}
            >
              {data.info?.psypact_provider ? (
                <Typography variant="body1" component="li" mb={1.5} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {psypact.label}
                  <Tooltip
                    placement="right"
                    title={
                      <Typography textAlign="start" variant="body2">
                        PSYPACT allows licensed psychologists to provide telehealth and short-term in-person care across participating states. PSYPACT providers may appear in searches for those states even if they do not list a license for that state on their profile.
                      </Typography>
                    }
                  >
                    <InfoIcon sx={{ color: "#0C8CE9", fontSize: 16 }} />
                  </Tooltip>
                </Typography>
              ) : null}
            </Paper>
          ) : null}
          {memberships.length > 0 ? (
            <Paper
              sx={{
                px: 3,
                pt: 3,
                pb: 1.5,
                mb: 2,
              }}
            >
              <Typography variant="h5" fontWeight={600} mb={3}>
                Memberships
              </Typography>
              <Grid container spacing={2}>
                {memberships.map((item) => (
                  <Grid item xs={12} sm={6} key={item.id}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        pl: 2,
                        mb: 3,
                      }}
                    >
                      {/* Bullet Point */}
                      <Typography
                        component="span"
                        variant="body1"
                        sx={{
                          listStyleType: "disc",
                          mr: 1,
                          mt: "4px",
                          display: "list-item",
                        }}
                      />
                      <Box>
                        <Typography variant="body1">
                          {item.data.title}
                        </Typography>
                        <Typography variant="body2" color="border.dark">
                          {item?.state?.name} {item?.data?.number} /{" "}
                          {item?.data?.year}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ) : null}
          {memberships.length > 0 ? <Grid item xs={12} /> : null}
          {data.training?.length > 0 ? (
            <Paper
              sx={{
                px: 3,
                pt: 3,
                pb: 1.5,
                mb: 2,
              }}
            >
              <Typography variant="h5" fontWeight={600} mb={3}>
                Trainings
              </Typography>
              <Grid container spacing={2}>
                {data.training.map((item) => (
                  <Grid item xs={12} sm={6} key={item.id}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        pl: 2,
                        mb: 3,
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body1"
                        sx={{
                          listStyleType: "disc",
                          mr: 1,
                          mt: "4px",
                          display: "list-item",
                        }}
                      />
                      <Box>
                        <Typography variant="body1">
                          {item.data?.type?.charAt(0).toUpperCase() +
                            item.data?.type?.slice(1)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="border.dark"
                          sx={{
                            overflowWrap: "break-word",
                            wordBreak: "normal",
                          }}
                        >
                          {item.data?.name} / {item.data?.focus},{" "}
                          {item.data?.year}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ) : null}
          {data.training?.length > 0 ? <Grid item xs={12} /> : null}
        </Paper>
      ) : null}
      {account_type === treatment_program && <PaymentInformation />}
      {isProviderDemographics ? (
        <Paper
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            Provider Demographics
          </Typography>
          {data.languages?.length > 0
            ? profileDemographics(data.languages, "Languages")
            : null}
          {data.gender?.length > 0
            ? profileDemographics(data.gender, "Gender")
            : null}
          {data.sexual_orientation?.length > 0
            ? profileDemographics(
              data.sexual_orientation,
              "Sexual Orientation",
              "sexual_orient",
            )
            : null}
          {data.race?.length > 0
            ? profileDemographics(data.race, "Race and Ethnicity", "race")
            : null}
          {data.religion?.length > 0
            ? profileDemographics(data.religion, "Religion", "religion")
            : null}
        </Paper>
      ) : null}
      {data.info?.office_hours_day || data.info?.office_hours_time ? (
        <Paper
          sx={{
            px: 3,
            pt: 3,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            Office Hours
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}
          >
            {data.info?.office_hours_day ? (
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Days
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 0,
                  }}
                >
                  {data.info?.office_hours_day
                    ?.split(",")
                    .map((item, index) => (
                      <Typography
                        component="li"
                        variant="body1"
                        sx={{
                          listStyleType: "disc",
                          ml: 2,
                          mb: 1.5,
                        }}
                        key={index}
                      >
                        {item.charAt(0).toUpperCase() + item.slice(1)}
                      </Typography>
                    ))}
                </Box>
              </Box>
            ) : null}
            {data.info?.office_hours_time ? (
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Working Hours
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 0,
                  }}
                >
                  {data.info?.office_hours_time
                    ?.split(",")
                    .map((item, index) => (
                      <Typography
                        component="li"
                        variant="body1"
                        sx={{
                          listStyleType: "disc",
                          ml: 2,
                          mb: 1.5,
                        }}
                        key={index}
                      >
                        {item.charAt(0).toUpperCase() + item.slice(1)}
                      </Typography>
                    ))}
                </Box>
              </Box>
            ) : null}
          </Box>
        </Paper>
      ) : null}

      {account_type === treatment_program ? (
        <>
          {data?.membersList?.length ? (
            <Paper
              sx={{
                p: 3,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignContent: "center",
                }}
              >
                <Typography variant="h5" fontWeight={600} mb={3}>
                  Meet Our Providers
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={400}
                  color="text.secondary"
                  mb={3}
                >
                  {calculateProviders()} Providers
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {data?.membersList?.map((element) => {
                  return (
                    <UserCard
                      key={element.id}
                      data={element}
                      onReferralClick={handleOpenReferralModal}
                      onShareClick={handleOpenShareModal}
                      handleOpenOtherConnection={handleOpenOtherConnection}
                      enableShare={false}
                      // enableReferral={false}
                      enableReferral={
                        element.user_referral && element.send_ref ? true : false
                      }
                      enableRemove={false}
                    />
                  );
                })}
              </Box>
            </Paper>
          ) : null}

          {/*  Below code is used to show map on the profile. Once Google API is provided it must be reused  */}

          {/* {data?.info?.address && (
            <Paper
              sx={{
                p: 3,
                mb: 2,
              }}
            >
              <Wrapper apiKey={googleApiKey}>
                <MapComponent
                  center={{
                    lat: parseFloat(data?.info?.address?.latitude),
                    lng: parseFloat(data?.info?.address?.longitude),
                  }}
                  zoom={10}
                >
                  <Marker
                    position={{
                      lat: parseFloat(data?.info?.address?.latitude),
                      lng: parseFloat(data?.info?.address?.longitude),
                    }}
                  />
                </MapComponent>
              </Wrapper>
            </Paper>
          )} */}
          <Faq />
        </>
      ) : null}

      <ReferalModal
        open={openReferral}
        onClose={handleCloseReferralModal}
        data={activeUser}
        isProfile={"user"}
      />
      <SendToFriendModal
        open={openShare}
        onClose={handleCloseShareModal}
        data={activeUser}
      />
      {openOtherNetwork && (
        <ConnectionsModal
          other={openOtherNetwork}
          selectedUser={selectedUser}
          accountType={"user"}
          handleClose={handleCloseConnections}
        />
      )}
    </>
  );
};

export default ProfileContent;
